import { AgentRouting, VoiceAiProvider } from '@pypestream/api-services/urql';
import {
  Pype,
  PypeBeta,
  PypeConfig,
  PypeStyle,
} from '../xstate/prostudio.api-service.types';
import { decodeFromBase64 } from './encode-base64';
import { toIntegrationConfig, toRoutingConfig } from './pype-agent-routing';

/** Genesys credentials, or a Contact Center voice inbox (`account_id` + `voice_inbox_id`). */
export interface LiveAgentConfig {
  integrator?: string;
  client_id?: string;
  client_secret?: string;
  grant_type?: string;
  instance_url?: string;
  o_auth_url?: string;
  account_id?: string;
  voice_inbox_id?: string;
}

export interface HandoffOptions {
  [skillTag: string]: {
    destination: string;
    type: string;
  };
}

export interface Channel {
  id: string;
  type?: 'widget' | 'twilio_voice' | 'vaapi';
  parent: string;
  env: 'live' | 'sandbox';
  streamId: string;
  pypeId: string;
  /** Bot the stream starts chat with — maps to a solution via botSolutionId. */
  botId?: string;
  logo: string;
  agentRouting: PypeConfig['agent_routing'];
  name: string;
  config?: PypeConfig['config'];
  /** Callback URL of the bound platform, part of its identity. */
  agentRoutingUrl?: string;
  /**
   * The pype's `agent_routing_config` verbatim — the bound connector's config as
   * stored. Kept raw so a save round-trips platform-specific credentials instead
   * of flattening them into `config.integration_headers`.
   */
  agentRoutingConfig?: Record<string, unknown> | null;
  botRetry?: PypeConfig['bot_retry'];
  style?: PypeStyle;
  beta?: PypeBeta;
  lastEdited?: string;
  customPypeCss?: string;
  analyticsEnabled?: boolean | null;
  /**
   * Contact Center inbox kind behind a `vaapi` channel — `vaapi_config.channel_type`
   * (`Channel::SMS`, `Channel::Whatsapp`, …). Only this is lifted from the config:
   * the rest of it holds the agent bot token.
   */
  vaapiChannelType?: string;
  twilioVoiceConfig?: {
    account_sid: string;
    auth_token: string;
    handoff_url: string;
    init_call_webhook_url: string;
    language: string;
    phone_number: string;
    speech_model: string;
    transcription_provider: string;
    tts_provider: string;
    voice: string;
    welcome_greeting: string;
    elevenlabs_audio_model: string;
    elevenlabs_voice_id: string;
    elevenlabs_similarity: number;
    elevenlabs_speed: number;
    elevenlabs_stability: number;
    elevenlabs_voice_metadata_b64?: string;
    elevenlabs_voice_metadata?: VoiceAiProvider;
    metadata_integration_info_b64?: string;
    live_agent_config?: LiveAgentConfig;
    voice_handoff_options_b64?: string;
    /** Multi-language overlay (PSTUDIO-2950); decoded by languagesFromTwilioConfig. */
    languages_config_b64?: string | null;
    handoff_options?: HandoffOptions;
    user_silence_timeout?: number | null;
    inbound_message_buffer_ms?: number | null;
    eot_threshold?: number | null;
    prompt_max_retries?: number | null;
    retry_prompt_prefix?: string | null;
    /**
     * Stored by the Channels API as a boolean OR as the strings 'true' /
     * 'undefined' / 'null' — read it through `isCallRecordingEnabled`, never
     * by truthiness.
     */
    enable_call_recording?: boolean | string | null;
  };
}

/** ISO string for a timestamp, or '' when it is absent or unparseable. */
const toIsoDate = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const VAAPI_CHANNEL_LABELS: Record<string, string> = {
  'Channel::SMS': 'SMS',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::Telegram': 'Telegram',
  'Channel::WebWidget': 'Web Widget',
};

/** Display label for a Contact Center (VAAPI) channel's `channel_type`. */
export const getVaapiChannelLabel = (channelType?: string): string =>
  (channelType && VAAPI_CHANNEL_LABELS[channelType]) || 'Contact Center';

/**
 * `channel_type` from a stream's `vaapi_config`. Typed as a String in the schema,
 * but the action passes the Pypes API body through (as with
 * `twilio_voice_config`), so accept either a JSON string or an object.
 */
const readVaapiChannelType = (config: unknown): string | undefined => {
  let parsed = config;
  if (typeof config === 'string') {
    try {
      parsed = JSON.parse(config);
    } catch {
      return undefined;
    }
  }
  if (!parsed || typeof parsed !== 'object') return undefined;
  const channelType = (parsed as { channel_type?: unknown }).channel_type;
  return typeof channelType === 'string' && channelType
    ? channelType
    : undefined;
};

export const getChannelsFromPypes = (pypes: Pype[]): Channel[] => {
  return (
    pypes?.flatMap(
      (pype) =>
        pype.streams?.flatMap((stream): Channel[] => {
          if (stream.twilio_voice_enabled) {
            if (!stream.id) {
              console.warn(
                '[getChannelsFromPypes] Skipping twilio_voice channel: stream.id is empty',
                { pypeId: pype.id, streamName: stream.name }
              );
              return [];
            }
            return [
              {
                id: stream.id,
                type: 'twilio_voice' as const,
                streamId: stream.id || '',
                parent: pype.name || '',
                env: pype.env?.toLowerCase() as 'live' | 'sandbox',
                pypeId: pype.id || '',
                botId: stream.start_chat_bot_id || undefined,
                logo: pype.logo || '',
                agentRouting: pype.agent_routing as AgentRouting,
                name: stream.name || '',
                twilioVoiceConfig: (() => {
                  const config =
                    stream.twilio_voice_config as Channel['twilioVoiceConfig'];
                  if (!config) return config;

                  let decoded: Channel['twilioVoiceConfig'] = { ...config };

                  if (
                    config.elevenlabs_voice_metadata_b64 &&
                    config.elevenlabs_voice_metadata_b64 !== 'null'
                  ) {
                    try {
                      decoded = {
                        ...decoded,
                        elevenlabs_voice_metadata: JSON.parse(
                          decodeFromBase64(config.elevenlabs_voice_metadata_b64)
                        ),
                      };
                    } catch (error) {
                      console.error(
                        'Failed to decode elevenlabs voice metadata:',
                        error
                      );
                    }
                  }

                  if (
                    config.metadata_integration_info_b64 &&
                    config.metadata_integration_info_b64 !== 'null'
                  ) {
                    try {
                      decoded = {
                        ...decoded,
                        live_agent_config: JSON.parse(
                          decodeFromBase64(config.metadata_integration_info_b64)
                        ),
                      };
                    } catch (error) {
                      console.error(
                        'Failed to decode live agent config:',
                        error
                      );
                    }
                  }

                  if (
                    config.voice_handoff_options_b64 &&
                    config.voice_handoff_options_b64 !== 'null'
                  ) {
                    try {
                      decoded = {
                        ...decoded,
                        handoff_options: JSON.parse(
                          decodeFromBase64(config.voice_handoff_options_b64)
                        ),
                      };
                    } catch (error) {
                      console.error('Failed to decode handoff options:', error);
                    }
                  }

                  return decoded;
                })(),
                lastEdited: stream.last_updated
                  ? toIsoDate(stream.last_updated)
                  : '',
                analyticsEnabled: stream.analytics_enabled,
              },
            ];
          }
          const vaapiChannels: Channel[] = [];
          if (stream.vaapi_enabled) {
            if (stream.id) {
              vaapiChannels.push({
                id: stream.id,
                type: 'vaapi',
                streamId: stream.id,
                parent: pype.name || '',
                env: pype.env?.toLowerCase() as 'live' | 'sandbox',
                pypeId: pype.id || '',
                botId: stream.start_chat_bot_id || undefined,
                logo: pype.logo || '',
                agentRouting: pype.agent_routing as AgentRouting,
                config: toIntegrationConfig(pype.agent_routing_config),
                agentRoutingUrl: pype.agent_routing_url ?? undefined,
                agentRoutingConfig: toRoutingConfig(pype.agent_routing_config),
                botRetry: pype.bot_retry,
                name: stream.name || '',
                vaapiChannelType: readVaapiChannelType(stream.vaapi_config),
                lastEdited: toIsoDate(stream.last_updated),
                analyticsEnabled: stream.analytics_enabled,
              });
            } else {
              console.warn(
                '[getChannelsFromPypes] Skipping vaapi channel: stream.id is empty',
                { pypeId: pype.id, streamName: stream.name }
              );
            }
          }
          // A Contact Center stream can also carry Pype widgets; those keep
          // their own rows (they have their own ids and settings pages).
          return vaapiChannels.concat(
            stream.widgets?.flatMap((widget): Channel[] => {
              if (!widget.id) {
                console.warn(
                  '[getChannelsFromPypes] Skipping widget channel: widget.id is empty',
                  {
                    pypeId: pype.id,
                    streamId: stream.id,
                    widgetName: widget.widget_name,
                  }
                );
                return [];
              }
              return [
                {
                  type: 'widget',
                  parent: pype.name || '',
                  env: pype.env?.toLowerCase() as 'live' | 'sandbox',
                  streamId: stream.id || '',
                  pypeId: pype.id || '',
                  botId: stream.start_chat_bot_id || undefined,
                  logo: pype.logo || '',
                  agentRouting: pype.agent_routing as AgentRouting,
                  config: toIntegrationConfig(pype.agent_routing_config),
                  agentRoutingUrl: pype.agent_routing_url ?? undefined,
                  agentRoutingConfig: toRoutingConfig(
                    pype.agent_routing_config
                  ),
                  botRetry: pype.bot_retry,
                  id: widget.id,
                  name: widget.widget_name || '',
                  // `new Date('').toISOString()` throws RangeError, so an
                  // unset/invalid last_updated would take down the whole channel
                  // list rather than just this pill (the twilio branch below was
                  // already guarded).
                  lastEdited: toIsoDate(widget.last_updated),
                  style: widget.style ? JSON.parse(widget.style) : undefined,
                  beta: widget.beta ? JSON.parse(widget.beta) : undefined,
                  customPypeCss: widget.custom_pype_css || '',
                  analyticsEnabled: stream.analytics_enabled,
                },
              ];
            }) || []
          );
        }) || []
    ) || []
  );
};
